"use client";

import React, { useState, useEffect } from "react";
import { Terminal, Send, Cpu, Shield, Activity, Globe, MapPin, Mail, Phone, CircleCheck, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSubmitting) {
      setProgressValue(0);
      interval = setInterval(() => {
        setProgressValue(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);
    } else {
      setProgressValue(100);
    }
    return () => clearInterval(interval);
  }, [isSubmitting]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="20200" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-teal-500/20 flex items-center justify-center border border-teal-500/50 shadow-[0_0_30px_-5px_rgba(20,184,166,0.4)]">
          <CircleCheck className="size-10 text-teal-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-slate-900">Transmission Received</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm">
          Initialize New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500/50 focus-visible:border-teal-500/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Professional email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email protocol"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-medium">Professional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@company.com" {...field} className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500/50 focus-visible:border-teal-500/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-medium">Company / Agency</FormLabel>
              <FormControl>
                <Input placeholder="Projectora Ltd" {...field} className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500/50 focus-visible:border-teal-500/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid frequency format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+64 9 123 4567" {...field} className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500/50 focus-visible:border-teal-500/50 transition-all duration-300 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="services_interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-700 font-medium mb-3 block">Areas of Expertise Required</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Project Management", "Programme Management", "ServiceNow Implementation", "Professional Training", "Field Services & Survey"].map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-3 p-3 rounded-sm border border-slate-100 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors group">
                  <input type="checkbox" className="appearance-none size-4 rounded-sm border border-slate-300 bg-white checked:bg-teal-600 checked:border-teal-600 transition-all cursor-pointer relative after:content-[''] after:absolute after:hidden checked:after:block after:left-[5px] after:top-[1px] after:w-[5px] after:h-[10px] after:border-white after:border-r-2 after:border-b-2 after:rotate-45" checked={field.value?.includes(option)} onChange={e => {
            const checked = e.target.checked;
            const current = field.value || [];
            if (checked) {
              field.onChange([...current, option]);
            } else {
              field.onChange(current.filter((val: string) => val !== option));
            }
          }} />
                  <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{option}</span>
                </label>)}
            </div>
            <FormDescription className="text-slate-400 text-xs">
              Select all functional domains relevant to your inquiry.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Project details are required",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-700 font-medium">Project Challenge</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your project objectives, current bottlenecks, and desired outcomes..." {...field} className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 min-h-[120px] focus-visible:ring-teal-500/50 focus-visible:border-teal-500/50 transition-all duration-300 resize-none rounded-sm" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-100 rounded-sm flex items-start gap-3">
          <CircleAlert className="size-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{submitError}</p>
        </div>}

      <div className="pt-2">
        {isSubmitting ? <div className="space-y-2">
            <div className="flex justify-between text-xs font-mono text-teal-600">
              <span>ESTABLISHING CONNECTION...</span>
              <span>{Math.round(progressValue)}%</span>
            </div>
            <Progress value={progressValue} className="h-2 bg-slate-100" />
          </div> : <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold tracking-tight py-6 shadow-lg shadow-teal-600/10 hover:shadow-teal-600/20 transition-all duration-300 rounded-sm">
            <Send className="mr-2 size-4" />
            SEND MESSAGE
          </Button>}
        
        {!isSubmitting && <div className="mt-4 flex justify-center">
            <Link to="#" onClick={e => {
          e.preventDefault();
          if (typeof window !== "undefined" && (window as any).Calendly) {
            (window as any).Calendly.initPopupWidget({
              url: 'https://calendly.com/projectora?hide_gdpr_banner=1'
            });
          }
          return false;
        }} className="text-xs font-mono text-slate-400 hover:text-teal-600 transition-all duration-300 uppercase tracking-widest flex items-center gap-2 group">
              <span className="w-8 h-px bg-slate-100 group-hover:bg-teal-100 transition-colors" />
              Projectora Consulting Limited
              <span className="w-8 h-px bg-slate-100 group-hover:bg-teal-100 transition-colors" />
            </Link>
          </div>}
      </div>
    </Form>;
}
export default function LetsTalkResults() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const initCalendly = () => {
      if (typeof window !== "undefined" && (window as any).Calendly) {
        (window as any).Calendly.initBadgeWidget({
          url: 'https://calendly.com/projectora',
          text: 'Schedule time with me',
          color: '#009385',
          textColor: '#ffffff',
          branding: true
        });
      }
    };
    initCalendly();
    window.addEventListener('load', initCalendly);
    return () => window.removeEventListener('load', initCalendly);
  }, []);
  return <section id="lets-talk-results" className="bg-background text-foreground relative py-24 overflow-hidden">
      <link href="https://assets.calendly.com/assets/external/widget.css" rel="stylesheet" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_15%_50%,rgba(20,184,166,0.05),transparent_50%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_85%_30%,rgba(15,23,42,0.03),transparent_50%)]" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/grid_pattern.png')] opacity-[0.02] bg-[length:40px_40px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-start transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-mono tracking-wider">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-600"></span>
                </span>
                CONSULTANCY STATUS: ACTIVE
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-slate-900">
                Let's Talk <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-teal-600">Results</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-md leading-relaxed">
                Direct access to our senior partners in Auckland. Initialize a secure connection to discuss your milestone objectives.
              </p>
            </div>

            <div className="w-full rounded-sm overflow-hidden border border-slate-200 bg-white shadow-2xl font-mono text-sm">
              <div className="bg-slate-900 px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-full bg-red-500/80" />
                  <div className="size-3 rounded-full bg-yellow-500/80" />
                  <div className="size-3 rounded-full bg-green-500/80" />
                </div>
                <div className="ml-4 text-xs text-slate-400">admin@projectora-hq:~</div>
              </div>
              <div className="p-6 space-y-4 text-slate-700">
                <div className="flex gap-2">
                  <span className="text-teal-600"></span>
                  
                </div>
                
                <div className="grid gap-4 pl-4 border-l border-slate-200 ml-1">
                  <div className="space-y-1">
                    <div className="text-slate-400 text-xs uppercase tracking-widest font-sans">Secure Line</div>
                    <div className="flex items-center gap-3 text-slate-900 hover:text-teal-600 transition-colors">
                      <Mail className="size-4" />
                      <Link to="mailto:hello@projectora.co.nz" className="hover:underline">Info@projectora.co.nz</Link>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-slate-400 text-xs uppercase tracking-widest font-sans">Auckland HQ</div>
                    <div className="flex items-center gap-3 text-slate-900">
                      <MapPin className="size-4" />
                      <span>8\133 Bayswater Avenue Auckland&nbsp;</span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-slate-400 text-xs uppercase tracking-widest font-sans">Consulting Hours</div>
                    <div className="flex items-center gap-3 text-slate-900">
                      <Activity className="size-4" />
                      <span>Mon-Fri: 08:30 - 17:30 NZDT</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <span className="text-teal-600"></span>
                  
                </div>
                <div className="pl-4 ml-1 text-xs space-y-1 text-slate-500">
                  
                  
                  
                </div>

                <div className="flex gap-2 animate-pulse">
                  <span className="text-teal-600"></span>
                  
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              {[{
              icon: Globe,
              label: "Services",
              href: "/ServicesPage"
            }, {
              icon: Shield,
              label: "Deliverables",
              href: "/DeliverablesPage"
            }, {
              icon: Cpu,
              label: "Training",
              href: "/TrainingPage"
            }].map((item, i) => <Link key={i} to={item.href} data-index={i} className="flex items-center gap-2 px-4 py-2 rounded-sm bg-slate-50 border border-slate-200 hover:bg-white hover:border-teal-500/30 transition-all group shadow-sm">
                  <item.icon className="size-4 text-slate-400 group-hover:text-teal-600 transition-colors" />
                  <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900">{item.label}</span>
                </Link>)}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-teal-500/10 to-slate-900/10 rounded-sm blur-xl opacity-50" />
              
              <div className="relative bg-white border border-slate-200 rounded-sm p-6 md:p-8 lg:p-10 shadow-2xl">
                <div className="mb-8 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 tracking-tight">Project Inquiry</h3>
                    <p className="text-sm text-slate-500">Submit your brief to initiate the scoping protocol.</p>
                  </div>
                  <Terminal className="size-6 text-slate-200" />
                </div>

                <FormProvider formKey="results_form_contact" formId="results_form_contact" sectionName="lets-talk-results" defaultValues={{
                name: "",
                email: "",
                company: "",
                phone: "",
                services_interest: [],
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="SEND MESSAGE" successMessage="Transmission received. Our senior partners are reviewing your project challenge.">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
      <script src="https://assets.calendly.com/assets/external/widget.js" type="text/javascript" async />
    </section>;
}